import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { useLocaleContext } from '@drone-js/core';
import { addMonths, isSameMonth, differenceInCalendarMonths } from 'date-fns';
import { getNumberOfMonths, getDefaultMonth } from './DatePicker.mappers.js';
import { getDateFnsLocaleOrDefault } from './util/index.js';

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var useDatePicker = function useDatePicker(props) {
  var _props$startDate, _props$nextButton, _props$prevButton;
  var _React$useState = React.useState('day'),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    calendarState = _React$useState2[0],
    setCalendarState = _React$useState2[1];
  var _React$useState3 = React.useState(),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    selectedNumberOfDayCalendar = _React$useState4[0],
    setSelectedNumberOfDayCalendar = _React$useState4[1];
  var isFirstRender = React.useRef(true);
  var containerRef = React.useRef(null);
  var containerGridRef = React.useRef(null);
  React.useEffect(function () {
    if (isFirstRender.current) return;
    if (calendarState === 'day') {
      var _containerRef$current, _container$querySelec;
      setSelectedNumberOfDayCalendar(undefined);
      var container = containerRef === null || containerRef === void 0 || (_containerRef$current = containerRef.current) === null || _containerRef$current === void 0 ? void 0 : _containerRef$current.querySelector('tbody');
      (_container$querySelec = container.querySelector('button[tabIndex="0"]')) === null || _container$querySelec === void 0 || _container$querySelec.focus();
    } else {
      var _containerGridRef$cur;
      containerGridRef === null || containerGridRef === void 0 || (_containerGridRef$cur = containerGridRef.current) === null || _containerGridRef$cur === void 0 || (_containerGridRef$cur = _containerGridRef$cur.querySelector('button[tabIndex="0"]')) === null || _containerGridRef$cur === void 0 || _containerGridRef$cur.focus();
    }
  }, [calendarState]);
  React.useEffect(function () {
    isFirstRender.current = false;
  }, []);
  var currentDate = new Date();
  var fromDate = (_props$startDate = props.startDate) !== null && _props$startDate !== void 0 ? _props$startDate : currentDate;
  var numberOfMonths = getNumberOfMonths(props.variant, props.scrollableMonthCount, props.startDate, props.endDate);
  var _React$useState5 = React.useState(getDefaultMonth(props.variant, props.isSingleDate, fromDate, props.date, props.dateRange, numberOfMonths, props.startDate, props.endDate)),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    calendarMonth = _React$useState6[0],
    setCalendarMonth = _React$useState6[1];
  var _React$useState7 = React.useState(calendarMonth),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    calendarYear = _React$useState8[0],
    setCalendarYear = _React$useState8[1];
  var _useLocaleContext = useLocaleContext(),
    agodaLocale = _useLocaleContext.locale;
  var locale = getDateFnsLocaleOrDefault(agodaLocale);
  var onMonthChange = function onMonthChange(month) {
    var _props$onMonthChange;
    // set selected month to 2nd calendar by reducing 1 month
    var selectedMonth = selectedNumberOfDayCalendar === 2 ? addMonths(month, -1) : month;
    if (props.variant === 'double' && selectedNumberOfDayCalendar === 1 && props.endDate && isSameMonth(selectedMonth, props.endDate)) {
      // adjust selected month to fit the calendar
      selectedMonth = addMonths(selectedMonth, -1);
    }
    if (props.variant === 'double' && selectedNumberOfDayCalendar === 2 && props.startDate && differenceInCalendarMonths(props.startDate, selectedMonth) === 1) {
      // adjust selected month to fit the calendar
      selectedMonth = addMonths(selectedMonth, 1);
    }
    setCalendarMonth(selectedMonth);
    setCalendarYear(selectedMonth);
    (_props$onMonthChange = props.onMonthChange) === null || _props$onMonthChange === void 0 || _props$onMonthChange.call(props, month);
  };
  var onYearChange = function onYearChange(year) {
    setCalendarYear(year);
  };
  var onCalendarChange = function onCalendarChange(newCalendarState, numberOfCalendar) {
    setCalendarState(newCalendarState);
    if (numberOfCalendar) {
      setSelectedNumberOfDayCalendar(numberOfCalendar);
    }
  };
  var dayPickerProps = _objectSpread(_objectSpread({}, props), {}, {
    locale: locale,
    currentDate: currentDate,
    onCalendarChange: onCalendarChange,
    onMonthChange: onMonthChange,
    month: calendarMonth
  });
  var monthYearPickerProps = {
    locale: locale,
    calendarYear: calendarYear,
    onYearChange: onYearChange,
    onMonthChange: onMonthChange,
    calendarMonth: calendarMonth,
    onCalendarChange: onCalendarChange,
    startDate: props.startDate,
    endDate: props.endDate,
    containerGridRef: containerGridRef,
    nextButton: (_props$nextButton = props.nextButton) === null || _props$nextButton === void 0 ? void 0 : _props$nextButton.call(props, calendarState),
    prevButton: (_props$prevButton = props.prevButton) === null || _props$prevButton === void 0 ? void 0 : _props$prevButton.call(props, calendarState)
  };
  return {
    calendarState: calendarState,
    setCalendarState: setCalendarState,
    currentDate: currentDate,
    locale: locale,
    calendarMonth: calendarMonth,
    setCalendarMonth: setCalendarMonth,
    onMonthChange: onMonthChange,
    calendarYear: calendarYear,
    setCalendarYear: setCalendarYear,
    containerGridRef: containerGridRef,
    onCalendarChange: onCalendarChange,
    dayPickerProps: dayPickerProps,
    monthYearPickerProps: monthYearPickerProps,
    containerRef: containerRef
  };
};

export { useDatePicker };
//# sourceMappingURL=useDatePicker.js.map
